import {
    Box, Hidden, Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import {
    Favorite as FavoriteIcon,
    EventAvailableOutlined as EventAvailableOutlinedIcon,
    FavoriteBorder as FavoriteBorderIcon,
    DeleteOutline as DeleteOutlineIcon,
    PhoneEnabledOutlined as PhoneEnabledOutlinedIcon,
} from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
    memo, useContext, useCallback,
} from 'react';
import type { JobSearchResultViewModel } from '../../../../types/jobs.types';
import Anchor from '../../foundation/Anchor';
import Button from '../../foundation/Button';
import CustomBR from '../../foundation/CustomBR';
import DateFormat from '../../foundation/DateFormat';
import { CenteredFlex } from '../../foundation/Flex';
import user_to_job_stateEnum from '../../../../common/user_to_job_state.enum';
import UserContext from '../../context/user.context';
import SimilarityGauge from '../SimilarityGauge';
import SvgLinkedinIcon from '../../svg/generated/components/LinkedinIcon';
import {
    IconButtonStyled, LinkedInButtonStyled, SimilarityGaugeStyled, RedIconButtonStyled, PhoneButtonStyled, DateStyled,
} from './JobItem.styles';

const isLinkedInJobUrl = (url: string): boolean => url?.indexOf('linkedin.com') !== -1;

export type JobItemActionsProps = {
    job: JobSearchResultViewModel
    onApplyClick?: (job: JobSearchResultViewModel) => void,
    onRemoveJobClick?: (job: JobSearchResultViewModel) => void,
    onSaveJobClick?: (job: JobSearchResultViewModel) => void,
    onUnsaveJobClick?: (job: JobSearchResultViewModel) => void,
    onWhatsAppClick?: (job: JobSearchResultViewModel) => void,
    onReportClick?: (job: JobSearchResultViewModel) => void,
    showRemoveJob?: boolean
    showDetailsLink?: boolean
    showPhoneBtn?: boolean
    onPhoneClick?: (job: JobSearchResultViewModel) => void
    showPhone?: boolean
}

export const JobItemActions = memo(({
    onSaveJobClick,
    onUnsaveJobClick,
    job,
    onRemoveJobClick,
    showDetailsLink,
    showRemoveJob,
    onApplyClick,
    onWhatsAppClick,
    onReportClick,
    showPhoneBtn,
    onPhoneClick,
    showPhone,
}: JobItemActionsProps) => {
    const onContactUsClick = useCallback(() => onWhatsAppClick(job), [job, onWhatsAppClick]);
    const onApplyHandlerClick = useCallback(() => onApplyClick(job), [job, onApplyClick]);
    const onPhoneHandlerClick = useCallback(() => onPhoneClick(job), [job, showPhone, onPhoneClick]);

    const user = useContext(UserContext);

    return <>
        <Box display="flex" justifyContent="space-between" width="100%">
            {showRemoveJob && <IconButtonStyled title="לא מעוניין במשרה" onClick={() => onRemoveJobClick(job)}>
                <DeleteOutlineIcon color="secondary" />
            </IconButtonStyled>}
            {job.isUserSaved ? <RedIconButtonStyled title="הסר משרה מהמועדפים" onClick={() => onUnsaveJobClick(job)}>
                <FavoriteIcon />
            </RedIconButtonStyled> : <IconButtonStyled title="הוסף משרה למועדפים" onClick={() => onSaveJobClick(job)}>
                <FavoriteBorderIcon color="secondary" />
            </IconButtonStyled>}
            <IconButtonStyled title="דווח למערכת על המשרה" onClick={() => onReportClick(job)}>
                <Typography color="secondary" fontSize={24}>⚠</Typography>
            </IconButtonStyled>
        </Box>
        <CustomBR />

        {job.defaultCvToJobSimilarity?.score > 0.1 && <SimilarityGaugeStyled>
            <SimilarityGauge tooltipText="התאמה למשרה על סמך קורות החיים שלך באמצעות AI" similarity={job.defaultCvToJobSimilarity.score} size="medium" />
        </SimilarityGaugeStyled>}
        {user && !job.defaultCvToJobSimilarity && <SimilarityGaugeStyled>
            <SimilarityGauge tooltipText={<Box>
                <Typography display="inline" variant="body2">לחישוב התאמה למשרה יש להוסיף קורות חיים,&nbsp;</Typography>
                <Anchor blank variant="body2" href={'/user/cvs'} text="הוסף קורות חיים" />
            </Box>} similarity={0} size="medium" />
        </SimilarityGaugeStyled>}
        {/* {!user && <SimilarityGaugeStyled>
            <SimilarityGauge tooltipText={<Box>
                <Typography display="inline" variant="body2">לחישוב התאמה למשרה יש להוסיף קורות חיים,&nbsp;</Typography>
                <Anchor variant="body2" onClick={ev => { ev.preventDefault(); window.modals.toggleRegisterModal(); }} text="הרשמה לאתר" />
            </Box>} similarity={0} size="medium" />
        </SimilarityGaugeStyled>} */}
        {job.userToJobState === user_to_job_stateEnum.LIKE && (
            <DateStyled>
                <CenteredFlex>
                    <EventAvailableOutlinedIcon />
                    <Typography display="inline">&nbsp;הגשת מועמדות ב&nbsp;</Typography>
                </CenteredFlex>
                <CenteredFlex>
                    <DateFormat fontWeight="bold" date={job.userToJobStateDate} />
                </CenteredFlex>
            </DateStyled>
        )}
        {job.userToJobState !== user_to_job_stateEnum.LIKE && (
            job.applyLink ? <>
                {isLinkedInJobUrl(job.applyLink) ? <LinkedInButtonStyled
                    title="הגש מועמדות"
                    size="large"
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={onApplyHandlerClick}
                >
                    <Box display="inline-flex" alignItems="center">
                        <Typography>הגש מועמדות</Typography>
                        <>
                            <Typography>&nbsp;&nbsp;</Typography>
                            <SvgLinkedinIcon
                                width={24}
                                height={24}
                            />
                        </>
                    </Box>
                </LinkedInButtonStyled> : ''}
                {!isLinkedInJobUrl(job.applyLink) ? <Button
                    title="הגש מועמדות למשרה"
                    size="large"
                    onClick={onApplyHandlerClick}
                    fullWidth
                    color="primary"
                >
                    <>
                        <Typography>הגש מועמדות באתר החברה</Typography>
                        <Typography>&nbsp;&nbsp;</Typography>
                        <LaunchIcon fontSize="medium" />
                    </>
                </Button> : ''}
            </> : <Button
                title="הגש מועמדות למשרה"
                size="large"
                onClick={onApplyHandlerClick}
                fullWidth
                color="primary"
            >
                הגש מועמדות
            </Button>
        )}
        <CustomBR />
        {job.phone?.startsWith('05') && <><Button
            // @ts-ignore
            target="_blank"
            onClick={onContactUsClick}
            title="צור קשר WhatsApp"
            size="large" fullWidth variant="outlined" color="primary">
            <Typography textTransform="initial" display="flex">
                <><WhatsAppIcon fontSize="medium" />&nbsp; WhatsApp</>
            </Typography>
        </Button>
        <CustomBR /></>}
        {showPhoneBtn && job.phone && <><PhoneButtonStyled>
            <Button fullWidth onClick={onPhoneHandlerClick} title={showPhone ? 'התקשרות' : 'הצג מספר טלפון'}>
                <><PhoneEnabledOutlinedIcon />&nbsp;{showPhone ? job.phone : 'הצג טלפון'}</>
            </Button>
        </PhoneButtonStyled><CustomBR /></>}
        {showDetailsLink
            && <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Button title="עמוד פרטי המשרה" size="large" onClick={ev => {
                    ev.preventDefault();
                    window.location.assign(`/job/details/${job.jobid}`);
                }} fullWidth color="secondary">פרטים</Button>
                <CustomBR />
            </Box>}
    </>;
});
